import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Markdown } from "@/lib/markdown";
import { LockedFeature } from "@/components/LockedFeature";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Building2, ArrowRight, Search, Sparkles, Loader2, Lightbulb, AlertTriangle, TrendingUp, Shield, KeyRound, Coins } from "lucide-react";

const SECTOR_GROUPS = {
  "Tech & Software": ["SaaS", "Enterprise Software", "Database", "Developer Platform", "DevOps Platform", "Cloud Infrastructure", "Creative Software", "Data & AI Platform", "Data Analytics", "Software", "Search", "Technology", "Consumer Tech", "Artificial Intelligence", "Mobile", "Telecom", "Imaging", "Internet", "Connected Fitness"],
  "Fintech & Finance": ["Fintech", "Banking", "Financial Services", "Payments Network", "Investment Banking", "Crypto", "Fintech Infrastructure", "Conglomerate"],
  "Consumer & Retail": ["Retail", "E-commerce", "Consumer Goods", "Food & Beverage", "Beverages", "Fast Food", "Coffee", "Apparel", "Toys", "Luxury Goods"],
  "Media & Social": ["Social Media", "Social", "Media & Entertainment", "Streaming", "Music", "Professional Network", "Video Rental"],
  "Marketplaces & Mobility": ["Marketplace", "Online Travel", "Ride-Sharing", "Food Delivery", "Grocery Delivery", "Super-App"],
  "Industrials & Auto": ["Automotive", "Luxury Automotive", "Aerospace", "Energy", "Semiconductors", "Consumer Electronics", "Electronics"],
};
const FILTERS = ["All", ...Object.keys(SECTOR_GROUPS), "Cautionary Tales"];
const primarySector = (ind) => (ind || "").split("/")[0].split("(")[0].trim();

export default function CaseStudies() {
  const { user } = useAuth();
  const isPro = user?.is_pro || user?.tier === "pro";
  const [studies, setStudies] = useState([]);
  const [company, setCompany] = useState("");
  const [open, setOpen] = useState(false);
  const [gen, setGen] = useState(null);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [group, setGroup] = useState("All");

  useEffect(() => { api.get("/case-studies").then((r) => setStudies(r.data)).catch(() => {}); }, []);

  if (!isPro) return (
    <LockedFeature title="Case Study Library" description="Deep breakdowns of legendary companies — business models, growth strategies, key decisions and mistakes — plus an AI engine to analyze ANY company you name."
      perks={["15+ curated legendary companies", "Generate a case study for any company", "Business model, growth & competitive edge", "Actionable insights you can apply"]} />
  );

  const generate = async () => {
    if (!company.trim()) return;
    setLoading(true); setGen(null); setOpen(true);
    try { const { data } = await api.post("/case-studies/generate", { company }); setGen(data); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not generate"); setOpen(false); }
    finally { setLoading(false); }
  };

  const q = query.trim().toLowerCase();
  const filtered = studies.filter((s) => {
    const matchesQuery = !q || s.company.toLowerCase().includes(q) || (s.industry || "").toLowerCase().includes(q) || (s.tagline || "").toLowerCase().includes(q);
    let matchesGroup = true;
    if (group === "Cautionary Tales") matchesGroup = /fraud|failure/i.test(s.industry || "");
    else if (group !== "All") matchesGroup = (SECTOR_GROUPS[group] || []).includes(primarySector(s.industry));
    return matchesQuery && matchesGroup;
  });

  return (
    <div className="space-y-8" data-testid="case-studies-page">
      <div>
        <span className="font-mono-eco text-xs uppercase tracking-widest text-slate-500">Case Study Engine · Pro</span>
        <h1 className="font-display text-3xl md:text-4xl font-bold text-white tracking-tight mt-1">Learn from the legends.</h1>
        <p className="text-slate-400 mt-2">Explore curated breakdowns — or have ECO analyze any company on demand.</p>
      </div>

      <div className="eco-card p-5 flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500" />
          <input data-testid="cs-company-input" value={company} onChange={(e) => setCompany(e.target.value)} onKeyDown={(e) => e.key === "Enter" && generate()} placeholder="Analyze any company (e.g., Notion, Coca-Cola, SpaceX)…" className="eco-input pl-10" />
        </div>
        <button data-testid="cs-generate-btn" onClick={generate} disabled={loading || !company.trim()} className="btn-accent">{loading ? <Loader2 size={16} className="animate-spin" /> : <><Sparkles size={16} /> Generate</>}</button>
      </div>

      <div>
        <div className="flex flex-col lg:flex-row lg:items-center gap-3">
          <div>
            <h2 className="font-display text-lg font-semibold text-white">Browse the library</h2>
            <p className="text-sm text-slate-500">{studies.length} legendary companies, broken down.</p>
          </div>
          <div className="relative flex-1 lg:max-w-xs lg:ml-auto">
            <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500" />
            <input data-testid="cs-filter-input" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search by name or industry…" className="eco-input pl-10" />
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mt-4" data-testid="cs-filter-chips">
          {FILTERS.map((f) => (
            <button key={f} data-testid={`cs-filter-${f.replace(/\s+/g, "-").toLowerCase()}`} onClick={() => setGroup(f)}
              className={`text-xs font-medium px-3.5 py-1.5 rounded-full border transition-colors ${group === f ? "bg-[#0055FF] text-white border-[#0055FF]" : "bg-white/5 text-slate-300 border-white/10 hover:border-white/25"}`}>
              {f}
            </button>
          ))}
        </div>

        <div className="flex items-center justify-between mt-5 mb-3">
          <span className="text-xs text-slate-500 font-mono-eco" data-testid="cs-result-count">{filtered.length} {filtered.length === 1 ? "result" : "results"}</span>
          {(query || group !== "All") && (
            <button data-testid="cs-clear-filters" onClick={() => { setQuery(""); setGroup("All"); }} className="text-xs text-slate-500 hover:text-white transition-colors">Clear</button>
          )}
        </div>

        {filtered.length === 0 ? (
          <div className="eco-card p-12 text-center" data-testid="cs-empty">
            <Building2 size={28} className="text-slate-600 mx-auto mb-3" />
            <p className="text-slate-400">No legends match your filter.</p>
            <p className="text-sm text-slate-600 mt-1">Try a different sector — or generate a custom case study above.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {filtered.map((s) => (
              <Link key={s.id} to={`/case-studies/${s.id}`} data-testid={`study-${s.id}`} className="eco-card eco-card-hover p-6 group">
                <div className="flex items-center justify-between">
                  <div className="h-11 w-11 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center"><Building2 size={22} className="text-[#4d8bff]" /></div>
                  <span className="eco-tag">{s.founded}</span>
                </div>
                <h3 className="font-display text-xl font-semibold text-white mt-4">{s.company}</h3>
                <p className="text-sm text-slate-400 mt-1">{s.tagline}</p>
                <div className="flex items-center justify-between mt-4">
                  <span className="text-xs text-slate-500">{s.industry}</span>
                  <ArrowRight size={16} className="text-slate-600 group-hover:text-[#4d8bff] transition-colors" />
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="cs-generated-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">{gen?.company || company}</DialogTitle></DialogHeader>
          {loading ? (
            <div className="flex flex-col items-center py-16 gap-3"><Loader2 className="animate-spin text-[#4d8bff]" /><p className="text-sm text-slate-500">ECO is analyzing {company}…</p></div>
          ) : gen ? (
            <div className="space-y-5">
              <div><span className="eco-tag">{gen.industry}</span> {gen.founded && <span className="eco-tag ml-2">{gen.founded}</span>}</div>
              {gen.tagline && <p className="text-slate-300 text-lg">{gen.tagline}</p>}
              <TextBlk icon={TrendingUp} title="Business model" body={gen.businessModel} color="#0055FF" />
              <ListBlk icon={Coins} title="Revenue streams" items={gen.revenueStreams} color="#10B981" />
              <TextBlk icon={TrendingUp} title="Growth strategy" body={gen.growthStrategy} color="#10B981" />
              <TextBlk icon={Shield} title="Competitive advantage" body={gen.competitiveAdvantage} color="#A855F7" />
              <div className="grid md:grid-cols-2 gap-4">
                <ListBlk icon={KeyRound} title="Key decisions" items={gen.keyDecisions} color="#0055FF" />
                <ListBlk icon={AlertTriangle} title="Mistakes made" items={gen.mistakesMade} color="#EF4444" />
              </div>
              <ListBlk icon={Lightbulb} title="Lessons learned" items={gen.lessonsLearned} color="#F59E0B" />
              <ListBlk icon={Sparkles} title="Actionable insights" items={gen.actionableInsights} color="#4d8bff" />
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TextBlk({ icon: I, title, body, color }) {
  if (!body) return null;
  return (<div className="eco-card p-5"><h3 className="flex items-center gap-2 font-display text-base font-semibold text-white mb-2"><I size={17} style={{ color }} /> {title}</h3><p className="text-sm text-slate-300 leading-relaxed">{body}</p></div>);
}
function ListBlk({ icon: I, title, items, color }) {
  if (!items?.length) return null;
  return (<div className="eco-card p-5"><h3 className="flex items-center gap-2 font-display text-base font-semibold text-white mb-3"><I size={17} style={{ color }} /> {title}</h3><ul className="space-y-2">{items.map((x, i) => <li key={i} className="flex items-start gap-2.5 text-sm text-slate-300"><span className="h-1.5 w-1.5 rounded-full mt-2 shrink-0" style={{ background: color }} /> {x}</li>)}</ul></div>);
}
